/**
 * @module SwipeSdk
 */
import { createSwipeIcon } from '../widgets/domRenderer';
import { setLocalStore } from '../shares/lib/localStore';
import { LocalStoreKeys } from '../shares/enums/localStoreKeys';
import { getSDKConfig } from '../shares/lib/getSDKConfig';
import {
  SwipeSDKConfig,
  SwipeSDKInstance,
} from '../shares/types/SwipeSDKConfigType';
import { SimulateLaunchOptions } from '../shares/model/simulate';
import { convertSdkConfig } from './config/setSdkConfig';
import { LogiSdk } from './logiSdk';

/**
 * Defines the structure of the SwipeSDK.
 * @deprecated
 */
export type SwipeSDK = {
  /**
   * Initializes the Swipe SDK.
   * @param {SwipeSDKConfig} config - The configuration object for the SDK.
   * @returns {Promise<SwipeSDKInstance>} A promise resolving to the initialized SDK instance.
   */
  init(config: SwipeSDKConfig): Promise<SwipeSDKInstance>;
};

/**
 * Swipe SDK class that manages the initialization and lifecycle of the SDK.
 * @implements {SwipeSDKInstance}
 * @deprecated
 */
class SwipeSdk implements SwipeSDKInstance {
  /**
   * Initializes the Swipe SDK.
   * @param {SwipeSDKConfig} config - The configuration object for the SDK.
   * @returns {Promise<SwipeSDKInstance>} A promise resolving to the initialized SDK instance.
   */
  static async init(config: SwipeSDKConfig): Promise<SwipeSDKInstance> {
    const instance = new SwipeSdk();
    const sdkConfig = convertSdkConfig(config);

    await LogiSdk.init(sdkConfig);

    const swipeSDKConfig = getSDKConfig();
    const gameList = swipeSDKConfig.swipeGames?.gameListConfig?.gameList || [];

    localStorage.removeItem(LocalStoreKeys.IS_SWIPE_SDK_INITIATED);

    if (
      config.loadWithInit &&
      swipeSDKConfig.enableSwipeGames &&
      gameList?.length
    ) {
      createSwipeIcon();
      setLocalStore(LocalStoreKeys.IS_SWIPE_SDK_INITIATED, true);
    }

    return instance;
  }

  /**
   * Retrieves the Swipe Launcher if conditions are met.
   * @returns {SwipeSdk | undefined} The SwipeSdk instance if initiated, otherwise undefined.
   */
  getSwipeLauncher(): SwipeSdk | undefined {
    LogiSdk.swipeGames.launch();
    return this;
  }

  /**
   * Removes the Swipe SDK from the application.
   * @returns {this} The instance of SwipeSdk.
   */
  removeSwipeSdk(): this {
    LogiSdk.swipeGames.remove();
    return this;
  }

  getMiniGames() {
    LogiSdk.miniGames.launch();
  }

  /**
   * Simulates the launch process with the provided options.
   * @param {SimulateLaunchOptions} options - The configuration options required to simulate the launch.
   * @return {Promise<void>} A promise that resolves when the simulation is completed or rejects if an error occurs.
   */
  simulateLaunch(options: SimulateLaunchOptions): Promise<void> {
    return LogiSdk.simulate.launch(options);
  }

  /**
   * Simulates the unmounting of a component or resource by invoking the `simulateUnmount` process.
   * @return {Promise<void>} A promise that resolves when the unmounting simulation is complete or rejects if an error occurs.
   */
  simulateUnmount(): Promise<void> {
    return LogiSdk.simulate.remove();
  }
}

/**
 * Swipe SDK entry point.
 * @type {SwipeSDK}
 */
const SwipeSDK: SwipeSDK = {
  init: SwipeSdk.init,
};

export default SwipeSdk;
